import _ from 'lodash';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { PersistenceLevel } from '@/core/flux.service';
import type { StatisticSummaryItemProps } from './StatisticSummaryItem.organism';

export class ViewStatisticSummariesStore extends BaseToolStore {
  static readonly storeName = 'sqViewStatisticSummariesStore';
  type = TREND_TOOLS.VIEW_STATISTIC_SUMMARIES;
  persistenceLevel: PersistenceLevel = 'NONE';

  initialize() {
    this.state = this.immutable(
      _.assign({}, BASE_TOOL_COMMON_PROPS, {
        items: [] as StatisticSummaryItemProps[],
        loading: false,
        hasNextPage: false,
        nameFilter: '',
        exactMatch: false,
        onlyInWorkbook: true,
      }),
    );
  }

  get isLoading() {
    return this.state.get('loading');
  }

  get items() {
    return this.state.get('items');
  }

  get hasNextPage() {
    return this.state.get('hasNextPage');
  }

  get nameFilter() {
    return this.state.get('nameFilter');
  }

  get exactMatch() {
    return this.state.get('exactMatch');
  }

  get onlyInWorkbook() {
    return this.state.get('onlyInWorkbook');
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    /**
     * Initializes the items to their default values. Done here so that this store doesn't dehydrate a bunch of
     * items when it is not in use.
     *
     * @param {Object} payload - Object containing export items
     */
    SET_VIEW_STAT_SUMMARIES: (payload: { items: StatisticSummaryItemProps[]; hasNextPage: boolean }) => {
      this.state.set('items', _.uniqBy([...this.state.get('items'), ...payload.items], 'id'));
      this.state.set('hasNextPage', payload.hasNextPage);
    },

    SET_LOADING_STAT_SUMMARIES: (payload: { loading: boolean }) => {
      this.state.set('loading', payload.loading);
    },

    DELETE_STAT_SUMMARY: (payload: { id: string }) => {
      const items = this.state.get('items');
      const filteredItems = _.filter(items, (item) => item.id !== payload.id);
      this.state.set('items', filteredItems);
    },

    SET_VIEW_STAT_SUMMARY_ELEMENT: (payload: { item: StatisticSummaryItemProps }) => {
      const items = this.state.get('items');
      this.state.set('items', _.sortBy([...items, { ...payload.item }], 'name'));
    },

    SET_STAT_SUMMARIES_NAME_FILTER: (payload: { name: string }) => this.state.set('nameFilter', payload.name),

    SET_STAT_SUMMARIES_EXACT_MATCH: (payload: { isExact: boolean }) => this.state.set('exactMatch', payload.isExact),

    SET_STAT_SUMMARIES_ONLY_IN_WORKBOOK: (payload: { onlyInWorkbook: boolean }) =>
      this.state.set('onlyInWorkbook', payload.onlyInWorkbook),

    RESET_VIEW_STAT_SUMMARIES: () => {
      this.state.set('items', []);
      this.state.set('hasNextPage', true);
    },
  };
}
